import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 * @property {HTMLElement} [successPopover] - Confirmation shown after adding a product.
 * @property {HTMLButtonElement} [successCloseButton] - Close control for the confirmation.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | undefined} */
  #successTimeout;

  /** @type {HTMLElement | null} */
  #successReturnFocus = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('keydown', this.#handleSuccessKeydown);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('keydown', this.#handleSuccessKeydown);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    if (this.#successTimeout) window.clearTimeout(this.#successTimeout);
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles successful product additions. The drawer stays closed until the
   * customer explicitly chooses "Ver carrito".
   * @param {CustomEvent<{
   *   resource?: { item_count?: number },
   *   data?: { source?: string, didError?: boolean }
   * }>} event
   */
  #handleCartAdd = (event) => {
    const isSuccessfulProductAdd =
      event.detail.data?.source === 'product-form-component' && event.detail.data?.didError !== true;

    if (isSuccessfulProductAdd) {
      this.#showSuccess();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  #showSuccess() {
    const popover = this.refs.successPopover;
    if (!popover) return;

    this.#successReturnFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    popover.hidden = false;
    window.requestAnimationFrame(() => {
      popover.setAttribute('data-open', '');
      this.refs.successCloseButton?.focus({ preventScroll: true });
    });

    if (this.#successTimeout) window.clearTimeout(this.#successTimeout);
    this.#successTimeout = window.setTimeout(() => this.closeSuccess(), 7000);
  }

  closeSuccess() {
    const popover = this.refs.successPopover;
    if (!popover || popover.hidden) return;

    popover.removeAttribute('data-open');
    window.setTimeout(() => {
      popover.hidden = true;
    }, 180);

    this.#successReturnFocus?.focus({ preventScroll: true });
    this.#successReturnFocus = null;
    if (this.#successTimeout) window.clearTimeout(this.#successTimeout);
  }

  openFromSuccess() {
    this.closeSuccess();
    this.showDialog();
  }

  /** @param {MouseEvent} event */
  handleSuccessBackdropClick(event) {
    if (event.target === event.currentTarget) this.closeSuccess();
  }

  /** @param {KeyboardEvent} event */
  #handleSuccessKeydown = (event) => {
    if (event.key !== 'Escape' || this.refs.successPopover?.hidden) return;
    this.closeSuccess();
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
